import * as qs from 'qs';
import _ from 'lodash';
import type Passport from '@patsnap-utils/passport/src/passport';
import { CommonApis } from './apis/common.api';
import type { HeaderService } from '@patsnap-utils/biz';
import { type IAddTrialExtendBody, NeedAddTrialActionType } from './apis/common.api.types';
import { isAxiosError } from './exception';
import { Site, SLD } from '@patsnap-utils/biz';
import { addTrialDeferred } from '@eureka/utils/account';
import { InitCookie } from '@patsnap/init-cookie';

const MAX_TRIAL_STORAGE_KEY = 'ADDTRIAL_clickcount';
const MAX_TRIAL_STORAGE_COUNT = 5;
const NO_PERMISSION_ADD_TRIAL_ERROR_CODE = 90112055;

const isNoPermissionPage = () =>
  typeof window !== 'undefined' && /^\/403\/?$/.test(window.location.pathname);

const redirectToNoPermissionPage = () => {
  if (typeof window === 'undefined') return;
  if (isNoPermissionPage()) return;
  window.location.href = new Site({ sld: SLD.EUREKA, pathname: '403/' }).href;
};

const redirectToAccountStatusPage = () => {
  if (typeof window === 'undefined') return;
  const pathName = new Site({ sld: SLD.EUREKA, pathname: 'account-status/' }).href;
  window.location.href = `${pathName}?account_status_from=${encodeURIComponent(window.location.href)}`;
};
const getNeedAddTrialErrorCode = (data: unknown) =>
  _.get(data, 'error_code') ??
  _.get(data, 'data.error_code') ??
  _.get(data, 'response.data.error_code') ??
  _.get(data, 'cause.error_code');

const isNoPermissionAddTrialError = (data: unknown) =>
  getNeedAddTrialErrorCode(data) === NO_PERMISSION_ADD_TRIAL_ERROR_CODE;

class AddTrialService {
  passport: Passport | void = undefined;
  private customProductSource?: string;

  ENABLE_AUTO_ADD_TRIAL = true;
  async addTrial(headerService: HeaderService, passport: Passport | void) {
    try {
      this.passport = passport;
      const grantedProductIds: string[] | undefined =
        headerService.account?._info.granted_product_ids;
      if (grantedProductIds === undefined) {
        throw new Error('Granted_product_ids is undefined!');
      }
      const extendBody = this.getAddTrialExtendBody();
      const empowerType = this.getEmpowerType(extendBody.from || (extendBody as any).start_from);
      // 如果已经在 account-status 页面，则不进行赋权
      if (window.location.pathname.includes('account-status')) {
        return;
      }

      const needAddTrialData = await CommonApis.needAddTrial(empowerType || '', extendBody);
      if (isNoPermissionAddTrialError(needAddTrialData)) {
        redirectToNoPermissionPage();
        return;
      }

      // 新版接口返回 { actionType } 对象，根据 actionType 决策
      const actionType = _.get(needAddTrialData, 'data.action_type', '') as
        | NeedAddTrialActionType
        | string;
      this.ENABLE_AUTO_ADD_TRIAL = false;

      switch (actionType) {
        case NeedAddTrialActionType.DIRECT_LOGIN:
          // 当前用户已有权限，直接进入产品，无需任何操作
          break;
        case NeedAddTrialActionType.GRANT_INDIVIDUAL_TENANT_PRIVILEGE: {
          // 后端已在本次请求中完成赋权，刷新页面使权限生效
          // 保护：防止因异常导致无限刷新
          const trialCounts = Number(sessionStorage.getItem(MAX_TRIAL_STORAGE_KEY) || 0);
          if (trialCounts >= MAX_TRIAL_STORAGE_COUNT) {
            sessionStorage.removeItem(MAX_TRIAL_STORAGE_KEY);
            window.location.href = new Site({ sld: SLD.EUREKA, pathname: '403/' }).href;
            throw new Error(`Trial counts is over ${MAX_TRIAL_STORAGE_COUNT}!`);
          }
          sessionStorage.setItem(MAX_TRIAL_STORAGE_KEY, String(trialCounts + 1));
          localStorage.removeItem('patsnap_header_account_info');
          console.warn('[AddTrial]', '赋权成功，刷新页面生效');
          this.passport?.removeLocal();
          window.location.reload();
          break;
        }
        case NeedAddTrialActionType.CONTACT_ADMIN:
          // 企业不允许创建个人租户，跳转 account-status 页面
          redirectToAccountStatusPage();
          break;
        // switch_to_individual_tenant / switch_and_grant_privilege / create_individual_tenant：
        // 需要切换/创建个人租户，属于交互流程，统一处理
        default:
          // 其他情况，跳转 account-status 页面
          redirectToAccountStatusPage();
          break;
      }
    } catch (error) {
      if (isNoPermissionAddTrialError(error)) {
        redirectToNoPermissionPage();
        return;
      }
      console.error('[AddTrial]', error);
    } finally {
      this.ENABLE_AUTO_ADD_TRIAL = false;
      addTrialDeferred.resolve(true);
    }
  }

  setProductSource(productSource: string) {
    this.customProductSource = productSource;
  }

  isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );
  }

  // [‘eureka-seo-patent-detail’] includes from 则return eureka-seo-rd
  getProductSource() {
    const searchParams = new URLSearchParams(window.location.search);
    const from = searchParams.get('from') || searchParams.get('start_from');
    // EurekaSEO新版专利详情页统计埋点 https://confluence.zhihuiya.com/pages/viewpage.action?pageId=278735616
    // 这个值是ssr-eureka 邮件注册的时候邮件的激活地址上会带上这个参数
    // eg.https://stage-passport.zhihuiya.com/public/free-register?login_email_binding_uri=https:%2F%2Fstage-account.zhihuiya.com%2Fpublic%2F%3Fchannel_source%3Dwest_eureka%26from%3Deureka%26redirect_uri%3Dhttps%253A%252F%252Fstage-eureka.zhihuiya.com%252Fhome%253FpatentId%253Df0c409ad-b154-4891-949e-6db0f6d87b32%2526start_from%253Deureka_landingpage%2526from%253Deureka-seo-patent-new%2526ai_search_text%253DUS20040251874A1%2526page%253Dpatent%26client_id%3D4355e565135c41b3b7395cbaf94f1b67%23%2Factive&product_name=eureka&client_id=4355e565135c41b3b7395cbaf94f1b67&recaptcha_token=xxxx
    if (from && from.startsWith('eureka-seo-patent-new')) {
      return 'eureka-seo-patent';
    }
    if (from && from.startsWith('eureka-seo-materials-')) {
      return 'eureka-seo-mse';
    }
    if (from && from.startsWith('eureka-seo-')) {
      return 'eureka-seo-rd';
    }
    if (from && from.startsWith('website-seo-')) {
      return 'website-seo';
    }

    const currentPath = window.location.pathname;
    if (currentPath.includes('/customize-list')) {
      return 'customize';
    }
    if (currentPath.includes('/agent-list')) {
      return 'agent_list';
    }
    const otherFrom = searchParams.get('start_from');
    if (otherFrom) {
      return otherFrom;
    }
    return 'eureka_home';
  }

  getSource() {
    const searchParams = new URLSearchParams(window.location.search);
    const from = searchParams.get('from') || searchParams.get('start_from');
    if (from && (from.startsWith('eureka-seo-') || from.startsWith('website-seo-'))) {
      return from;
    }
    return InitCookie.getCampaignName();
  }

  getCookieExtendBody() {
    return {
      source: this.getSource(),
      entry_channel: InitCookie.getEntryChannel(),
      product_source: this.customProductSource || this.getProductSource(),
    };
  }

  getAddTrialExtendBody(): IAddTrialExtendBody {
    const hash = window.location.hash;
    const queryString = hash.includes('?') ? '?' + hash.split('?')[1] : window.location.search;

    const query = qs.parse(queryString, {
      ignoreQueryPrefix: true,
    });
    // warning: 直接全部给后端可能会带有多余的参数
    // @ts-ignore
    return {
      ...query,
      ...this.getCookieExtendBody(),
      media_type: this.isMobileDevice() ? 'Mobile' : '',
    };
  }

  getEmpowerType(from?: string) {
    const SEO_SOURCES = [
      'eureka-seo-report-detail',
      'eureka-seo-report-list',
      'eureka-seo-article',
      'eureka-seo-insight',
      'discovery_seo_company',
    ];
    if (from && SEO_SOURCES.includes(from)) return 'RD_SEO';
  }
}

export const addTrialService = new AddTrialService();

export function errorHandleIgnore403(e: Error) {
  return isAxiosError(e) && e.response?.status === 403;
}
